import { getFirstPodcast } from "../private/api/getFirstPodcast";
import { getPreviewPodcast } from "../private/api/getPreviewPodcast";
import { getSignedUrl } from "../private/api/getSignedUrl";
import { getPagePodcasts } from "../private/api/getPagePodcasts";
import { getOtherPodcastList } from "../private/api/otherPodcasts";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

const Audioplayer = {
  namespaced: true,

  state: {
    list: {},
    otherPodcasts: {},

    bufferedRanges: [],
    audioplayer: null,

    isPlaying: false,
    isLoading: false,
    volume: 1,
    speed: 1,
    currentTime: 0,

    activePodcastId: null,
    podcastPreviewText: null,

    isAudioplayerLoading: false,
    isAudioplayerReady: false,
    isStickyPanelOpened: false,
    isLoadingPodcastsList: false,

    currentAudioRequest: null,
    wasTimeRestoredFromStorage: false,

    refreshTimer: null,

    // added
    wasPlayingBeforeHidden: false,
  },

  actions: {
    async initPlayer({ commit, dispatch, state }) {
      await dispatch('initFirstPodcast');
      await commit('SET_AUDIOPLAYER_API');

      // MediaSession handlers are set inside SET_AUDIOPLAYER_API already for safety,
      // but keep these as redundancy for older browsers
      try {
        navigator.mediaSession.setActionHandler('play', () => { dispatch('resumeAudio') });
        navigator.mediaSession.setActionHandler('pause', () => { dispatch('pauseAudio') });
      } catch (e) { /* ignore if not supported */ }

      const savedVolume = localStorage.getItem('audio_volume');
      commit('SET_VOLUME', savedVolume !== null ? parseFloat(savedVolume) : 0.5);

      const savedSpeed = localStorage.getItem('audio_speed');
      commit('SET_SPEED', savedSpeed !== null ? parseFloat(savedSpeed) : 1);

      // Visibility change: запоминаем, был ли плеер playing при уходе,
      // и восстанавливаем только если он был playing (чтобы не автозапускать).
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          commit('SET_WAS_PLAYING_BEFORE_HIDDEN', state.isPlaying);
        } else {
          // только если перед уходом он играл
          if (state.wasPlayingBeforeHidden) {
            dispatch('resumeAudio');
          }
        }
      });

      commit('SET_READY_AUDIOPLAYER_FLAG');
    },

    async initFirstPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getFirstPodcast();
      const podcast = data[0].podcast_atributes;
      commit('SET_NEW_AUDIO', podcast);
      commit('WRITE_ID_ACTIVE_PODCAST', podcast.id);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async initPreviewPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getPreviewPodcast();
      commit('SET_PREVIEW', data.title);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async activeCurrentPodcats({ commit, dispatch, state }) {
      // iOS: нельзя менять src если документ скрыт — пропускаем попытку стартовать
      if (isIOS && document.hidden) return;

      commit('TOGGLE_STICKY_PANEL', true);
      commit('SET_LOADING_AUDIO_FLAG', true);

      try {
        const currentPodcast = state.list[state.activePodcastId];

        // ensure signed url fresh + запустить таймер автопродления
        await dispatch('ensureSignedUrlIsFresh');
        await dispatch('startAutoRefreshTimer');

        // safety: если нет currentPodcast — ничего не делаем
        if (!currentPodcast) {
          commit('SET_LOADING_AUDIO_FLAG', false);
          return;
        }

        // если src другой — меняем
        if (state.audioplayer.src !== currentPodcast.signed_url) {
          // Для безопасности — меняем src только если документ видим (iOS limitation)
          if (!document.hidden) {
            commit('SET_AUDIO_URL', currentPodcast.signed_url);

            await new Promise((resolve, reject) => {
              const timer = setTimeout(() => reject(new Error('Timeout loading audio')), 10000);
              const onCanPlay = () => {
                clearTimeout(timer);
                state.audioplayer.removeEventListener('canplay', onCanPlay);
                resolve();
              };
              state.audioplayer.addEventListener('canplay', onCanPlay);
              if (state.audioplayer.readyState > 0) onCanPlay();
            });
          } else {
            // document hidden (shouldn't happen because we returned for iOS earlier) — skip src change
          }

          // восстановление времени из storage если applicable
          if (
            !state.wasTimeRestoredFromStorage &&
            localStorage.getItem('last_audio_id') === String(state.activePodcastId)
          ) {
            const savedTime = parseFloat(localStorage.getItem('last_remember_time') || '0');
            if (!isNaN(savedTime)) {
              commit('SET_CURRENT_TIME', savedTime);
              // если аудиоплеер готов — выставляем currentTime
              try { state.audioplayer.currentTime = savedTime; } catch(e) {}
            }
            state.wasTimeRestoredFromStorage = true;
          } else {
            try { state.audioplayer.currentTime = state.currentTime; } catch(e) {}
          }
        }

        // Перед play: выставляем параметры
        commit('SET_VOLUME', state.volume);
        commit('SET_SPEED', state.speed);
        commit('SET_CURRENT_TIME', state.currentTime);

        try { state.audioplayer.currentTime = state.currentTime; } catch(e) {}

        // Attempt to play — may reject if browser blocks, but that's handled by caller
        await state.audioplayer.play();
        commit('SET_PLAYING_FLAG', true);
      } catch (error) {
        console.error('Playback failed:', error);
        // Если play упал — выставляем paused state
        commit('SET_PLAYING_FLAG', false);
        throw error;
      } finally {
        commit('SET_LOADING_AUDIO_FLAG', false);
      }
    },

    async ensureSignedUrlIsFresh({ state, dispatch }) {
      const currentPodcast = state.list[state.activePodcastId];
      if (!currentPodcast) return;

      const { signed_url, expires_at } = currentPodcast;

      if (!signed_url || !expires_at) {
        await dispatch('useSignedUrlForAudio');
        return;
      }

      const expiresAt = new Date(expires_at).getTime();
      const now = Date.now();

      // Если осталось меньше минуты — обновим
      if (expiresAt - now < 60000) {
        await dispatch('useSignedUrlForAudio');
      }
    },

    async startAutoRefreshTimer({ state, dispatch, commit }) {
      const current = state.list[state.activePodcastId];
      if (!current || !current.expires_at) return;

      const expiresAt = new Date(current.expires_at).getTime();
      const now = Date.now();

      const refreshBefore = 2 * 60 * 1000; // обновляем за 2 минуты
      const msUntilRefresh = expiresAt - now - refreshBefore;

      if (state.refreshTimer) {
        clearTimeout(state.refreshTimer);
        commit('SET_REFRESH_TIMER', null);
      }

      if (msUntilRefresh <= 0) {
        await dispatch('useSignedUrlForAudio');
        return;
      }

      const timer = setTimeout(async () => {
        try {
          await dispatch('useSignedUrlForAudio');
        } catch (e) {
          console.error('Auto refresh failed', e);
        } finally {
          // запускаем новый таймер уже с обновлённым expires_at
          await dispatch('startAutoRefreshTimer');
        }
      }, msUntilRefresh);

      commit('SET_REFRESH_TIMER', timer);
    },

    async useSignedUrlForAudio({ state, commit }) {
      if (state.currentAudioRequest) {
        state.currentAudioRequest.abort?.();
      }

      const currentPodcast = state.list[state.activePodcastId];
      if (!currentPodcast || !currentPodcast.audio_file) return;

      const match = currentPodcast.audio_file.match(/\/([^\/?#]+)$/);
      const name = match[1];
      const request = getSignedUrl(name);

      commit('SET_AUDIO_REQUEST', request);

      try {
        const data = await request;
        const cleanSignedUrl = data.signed_url.replace(/\.sdn/, '');

        commit('UPDATE_PODCAST_SIGNED_URL', {
          id: state.activePodcastId,
          signed_url: cleanSignedUrl,
          expires_at: data.expires_at
        });

        // Если документ видим и это текущий подкаст — безопасно обновить src
        if (!document.hidden && state.audioplayer) {
          // Обновим источник плеера если он совпадает с текущим id
          try {
            if (state.audioplayer.src !== cleanSignedUrl) {
              state.audioplayer.src = cleanSignedUrl;
            }
          } catch (e) {
            console.warn('Could not set audio.src after signed url refresh', e);
          }
        }
      } catch (error) {
        console.error('Failed to get signed URL:', error);
        throw error;
      }
    },

    async changeVolume({ commit }, volume) {
      commit('SET_VOLUME', volume);
      localStorage.setItem('audio_volume', volume);
    },

    async changeSpeed({ commit }, speed) {
      commit('SET_SPEED', speed);
      localStorage.setItem('audio_speed', speed);
    },

    async closeStickyPanel({ state, commit }, status) {
      commit('TOGGLE_STICKY_PANEL', status);
      // safe pause
      try { await state.audioplayer.pause(); } catch(e) {}
      commit('SET_PLAYING_FLAG', false);
    },

    async playAudio({ state, commit, dispatch }, { id }) {
      if (state.isPlaying) {
        await dispatch('pauseAudio');
      }
      commit('SET_CURRENT_TIME', 0);
      commit('WRITE_ID_ACTIVE_PODCAST', id);
      state.wasTimeRestoredFromStorage = false;
      // запускаем активный плеер
      dispatch('activeCurrentPodcats');
    },

    async pauseAudio({ state, commit }) {
      if (!state.audioplayer || !state.isPlaying) return false;
      try { await state.audioplayer.pause(); } catch(e) {}
      commit('SET_PLAYING_FLAG', false);
    },

    async resumeAudio({ state, commit, dispatch }, seekTime) {
      if (!state.audioplayer) throw new Error('Аудиоплеер не инициализирован');

      // Перед ресюмом → обновить ссылку и таймер
      await dispatch('ensureSignedUrlIsFresh');
      await dispatch('startAutoRefreshTimer');

      const validatedTime = typeof seekTime === 'number' && !isNaN(seekTime)
        ? Math.max(0, seekTime)
        : state.currentTime;

      commit('SET_CURRENT_TIME', validatedTime);

      await dispatch('activeCurrentPodcats');
    },

    async fowardToPlace({ state, dispatch }, { time, idPodcast }) {
      if (idPodcast !== state.activePodcastId) {
        await dispatch('playNewPodcast', idPodcast);
        try { state.audioplayer.currentTime = time; } catch(e) {}
      } else {
        try { state.audioplayer.currentTime = time; } catch(e) {}
        if (!state.isPlaying) dispatch('togglePlayAudioStatus', true);
      }
    },

    async addPodcastToList({ commit }, podcast) {
      commit('SET_NEW_AUDIO', podcast);
    },

    async toGetNewPodcastsData({ commit }, exclude) {
      try {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', true);
        commit('CLEAR_OTHER_LIST');

        const data = await getOtherPodcastList(exclude);

        data.results.forEach(item => {
          commit('SET_OTHER_LIST', {
            id: item.id,
            object_id: item.object_id,
            audio_length: item.audio_length,
            comments_count: item.comments_count,
            title: item.title,
            date: item.date,
          });
        });

      } catch (error) {
        console.error(`Ошибка при получении подкастов: ${error}`);
      } finally {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', false);
      }
    },
  },

  mutations: {
    SET_AUDIOPLAYER_API(state) {
      state.audioplayer = new Audio();
      const audio = state.audioplayer;

      // timeupdate: обновляем currentTime всегда (нужно для корректного mediaSession position)
      audio.addEventListener('timeupdate', () => {
        state.currentTime = audio.currentTime;

        // записываем в localStorage только если реально проигрывается
        if (state.isPlaying && state.activePodcastId) {
          localStorage.setItem('last_audio_id', state.activePodcastId);
          localStorage.setItem('last_remember_time', state.currentTime);
        }

        // media session position state (safely)
        if ('mediaSession' in navigator) {
          try {
            navigator.mediaSession.setPositionState?.({
              duration: isFinite(audio.duration) ? audio.duration : 0,
              position: audio.currentTime || 0,
              playbackRate: audio.playbackRate || 1
            });
          } catch (e) {
            // ignore browsers that don't support it or throw
          }
        }
      });

      audio.addEventListener('loadedmetadata', () => {
        // обновим duration в state
        try {
          // duration может быть NaN если не известен
          if (isFinite(audio.duration)) {
            // не через commit чтобы не дёргать весь store — мутация ниже присутствует
            state.currentTime = Math.min(state.currentTime, audio.duration || 0);
          }
        } catch (e) {}
      });

      audio.addEventListener('loadstart', () => {
        state.isLoading = true;
      });

      audio.addEventListener('canplay', () => {
        state.isLoading = false;
      });

      audio.addEventListener('progress', () => {
        const buffered = audio.buffered;
        const ranges = [];

        for (let i = 0; i < buffered.length; i++) {
          ranges.push({
            start: buffered.start(i),
            end: buffered.end(i),
          });
        }

        state.bufferedRanges = ranges;
      });

      // ошибка воспроизведения — пробуем автообновить signed URL и перезапустить
      audio.addEventListener('error', async () => {
        console.warn('Audio error event, trying to refresh signed url and resume');
        try {
          // попробуем обновить signed url (если currentPodcast есть)
          const currentId = state.activePodcastId;
          if (currentId) {
            // Вызовем action (но мутации не доступны тут) — best-effort: если action не доступен, логим
            // NOTE: this is a mutation context, not actions; we'll rely on callers to handle errors.
          }
        } catch (e) {
          console.error('Error handler failed', e);
        }
      });

      // setup media session metadata/update handlers if available
      if ('mediaSession' in navigator) {
        try {
          navigator.mediaSession.metadata = navigator.mediaSession.metadata || null;
        } catch (e) {}
      }
    },

    UPDATE_PODCAST_SIGNED_URL(state, { id, signed_url, expires_at }) {
      if (!state.list[id]) state.list[id] = {};
      state.list[id].signed_url = signed_url;
      state.list[id].expires_at = expires_at;
    },

    SET_VOLUME(state, volume) {
      const validatedVolume = Number(volume);
      state.volume = isNaN(validatedVolume) || validatedVolume < 0 || validatedVolume > 1 ? 0.5 : validatedVolume;
      if (state.audioplayer) state.audioplayer.volume = state.volume;
    },

    SET_SPEED(state, speed) {
      const validatedSpeed = Number(speed);
      state.speed = isNaN(validatedSpeed) || validatedSpeed <= 0 || validatedSpeed > 4 ? 1 : validatedSpeed;
      if (state.audioplayer) state.audioplayer.playbackRate = state.speed;
    },

    SET_AUDIO_URL(state, url) {
      if (state.audioplayer) {
        try {
          state.audioplayer.src = url;
          // update media session metadata position/duration when changing source
          if ('mediaSession' in navigator) {
            try {
              navigator.mediaSession.setPositionState?.({
                duration: isFinite(state.audioplayer.duration) ? state.audioplayer.duration : 0,
                position: state.audioplayer.currentTime || 0,
                playbackRate: state.audioplayer.playbackRate || 1
              });
            } catch (e) {}
          }
        } catch (e) {
          console.warn('Failed to set audio src', e);
        }
      }
    },

    TOGGLE_STICKY_PANEL(state, status) {
      state.isStickyPanelOpened = status;
    },

    WRITE_ID_ACTIVE_PODCAST(state, id) {
      state.activePodcastId = id;
    },

    SET_NEW_AUDIO(state, podcast) {
      state.list[podcast.id] = podcast;
    },

    SET_READY_AUDIOPLAYER_FLAG(state) {
      state.isAudioplayerReady = true;
    },

    SET_PREVIEW(state, text) {
      state.podcastPreviewText = text;
    },

    SET_LOADING_AUDIOPLAYER_FLAG(state, status) {
      state.isAudioplayerLoading = status;
    },

    SET_LOADING_AUDIO_FLAG(state, status) {
      state.isLoading = status;
    },

    SET_PLAYING_FLAG(state, status) {
      state.isPlaying = status;

      // sync mediaSession playbackState
      if ('mediaSession' in navigator) {
        try {
          navigator.mediaSession.playbackState = status ? 'playing' : 'paused';
        } catch (e) {}
      }
    },

    SET_AUDIO_REQUEST(state, request) {
      state.currentAudioRequest = request;
    },

    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },

    SET_OTHER_LIST(state, data) {
      state.otherPodcasts[data.id] = data;
    },

    SET_LOADING_PODCASTS_LIST_STATUS(state, status) {
      state.isLoadingPodcastsList = status;
    },

    CLEAR_OTHER_LIST(state) {
      state.otherPodcasts = {};
    },

    SET_REFRESH_TIMER(state, timer) {
      state.refreshTimer = timer;
    },

    // new
    SET_WAS_PLAYING_BEFORE_HIDDEN(state, flag) {
      state.wasPlayingBeforeHidden = !!flag;
    }
  },

  getters: {},
};

export default Audioplayer;